import type { ReactNode } from 'react';
import { Ban, ChevronsLeft, Clock } from 'lucide-react';
import type { ActivityType } from '~/types/customer-detail.types';
import { ActivityIcon, getActivityTextColor } from './activity-icon';
import { HostessAvatar } from './hostess-avatar';

export interface ActivityRowData {
  id: string;
  store: string;
  type: ActivityType;
  date: string;
  activityDateIso: string;
  customerName?: string;
  hostess: {
    id: string;
    name: string;
    avatar: any;
    status: 'working' | 'absent';
    role?: string;
  };
  isMyActivity: boolean;
}

interface ActivityRowProps {
  activity: ActivityRowData;
  customerCell: ReactNode;
  onActionClick: (activity: ActivityRowData) => void;
  onEditDateClick?: (activity: ActivityRowData) => void;
}

function HostessStatus({ hostess, storeName }: { hostess: ActivityRowData['hostess']; storeName?: string }) {
  const isWorking = hostess.status === 'working';
  return (
    <span className="gap-xs text-2xs flex items-center">
      <span
        className={`inline-block size-[1em] rounded-full ${
          isWorking ? 'bg-success' : 'bg-stone-400'
        }`}
      />
      <span
        className={`flex-none font-bold ${
          isWorking ? 'text-success' : 'text-stone-600'
        }`}
      >
        {isWorking ? (storeName || '在籍') : '退職'}
      </span>
    </span>
  );
}

export function ActivityRow({
  activity,
  customerCell,
  onActionClick,
  onEditDateClick,
}: ActivityRowProps) {
  const textColor = getActivityTextColor(activity.type, activity.isMyActivity);

  return (
    <li
      className={`px-lg gap-md h-(--h-list) relative grid grid-cols-5 items-center leading-none ${
        activity.isMyActivity ? 'bg-brand/5' : ''
      }`}
    >
      {/* 店舗名（右上） */}
      <strong className="py-sm bg-secondary text-primary text-2xs absolute end-0 top-0 rounded-bl-lg border-b border-s px-[.7em] font-bold leading-snug tracking-tight">
        {activity.store}
      </strong>

      {/* アクティビティアイコン + 顧客情報 */}
      <div className="gap-md col-span-3 flex items-center">
        <div className={`text-2xs space-y-sm text-center tracking-normal ${textColor}`}>
          <ActivityIcon
            type={activity.type}
            isMyActivity={activity.isMyActivity}
          />
          <strong className="block font-bold opacity-70">{activity.type}</strong>
        </div>
        {customerCell}
      </div>

      {/* ホステス情報 */}
      <div className="gap-sm col-span-2 flex items-center">
        <ChevronsLeft
          className={`w-lg -my-md ${activity.isMyActivity ? 'text-brand' : 'text-stone-400'}`}
        />
        <div className={`flex items-center ${activity.isMyActivity ? 'gap-md' : 'gap-sm'}`}>
          <div className="block flex-none">
            <HostessAvatar
              avatar={activity.hostess.avatar}
              name={activity.hostess.name}
              isMyActivity={activity.isMyActivity}
            />
          </div>
          <strong
            className={`block font-bold leading-none ${activity.isMyActivity ? 'space-y-sm flex-none' : 'space-y-sm'}`}
          >
            <span className="block">{activity.hostess.name}</span>
            {activity.isMyActivity ? (
              <div className="gap-md flex items-start">
                {onEditDateClick && (
                  <button
                    type="button"
                    onClick={() => onEditDateClick(activity)}
                    className="space-y-sm grid place-items-center active:scale-95 transition-transform"
                  >
                    <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-gray-400">
                      <Clock className="size-[1.35rem]" />
                    </div>
                    <strong className="block text-xs font-bold leading-none tracking-tight text-gray-500">
                      時刻訂正
                    </strong>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onActionClick(activity)}
                  className="space-y-sm grid place-items-center active:scale-95 transition-transform"
                >
                  <div className="flex size-[2.6rem] items-center justify-center rounded-full border border-red-400 text-red-600">
                    <Ban className="size-[1.35rem]" />
                  </div>
                  <strong className="block text-xs font-bold leading-none tracking-tight text-red-500">
                    取消
                  </strong>
                </button>
              </div>
            ) : (
              <HostessStatus hostess={activity.hostess} storeName={activity.store} />
            )}
          </strong>
        </div>
      </div>
    </li>
  );
}
